import React, { useState, useRef, useEffect } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import {
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CustomTextField from "./CustomTextField";
import { Clear, InsertInvitation } from "@mui/icons-material";
import "react-multi-date-picker/styles/layouts/mobile.css";
import "./CustomDateRange.css";
import { setSingleAlertObj, SingleAlertInfo } from "../../redux/slice/commonSlice";
import { useDispatch } from "react-redux";
import { Controller } from "react-hook-form";

interface CustomDateRangeProps {
  label?: string;
  placeholder?: string;
  control: any;
  controllerName: string;
  month?: number;
  dateRangeMonths?: number;
  onChange?: (fromDate: DateObject | null, toDate: DateObject | null) => void;
  onClear?: boolean;
  zIndex?: number;
}

const CustomDateRange: React.FC<CustomDateRangeProps> = ({
  label,
  placeholder,
  control,
  controllerName,
  month,
  dateRangeMonths,
  onChange,
  onClear,
  zIndex,
}) => {
  const theme = useTheme();
  const textFieldRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);
  
  // Use refs to track prop changes
  const prevDateRangeMonthsRef = useRef<number | undefined>(dateRangeMonths);
  const prevOnClearRef = useRef<boolean | undefined>(onClear);
  const shouldUpdateFromMonthsRef = useRef(false);
  const shouldClearRef = useRef(false);

  const getDefaultDateRange = (): string => {
    if (dateRangeMonths) {
      const endDate = new DateObject();
      const startDate = new DateObject().subtract(dateRangeMonths, "months");
      return `${startDate.format("MM/DD/YYYY")} - ${endDate.format("MM/DD/YYYY")}`;
    }
    return "";
  };

  // Track when dateRangeMonths changes
  useEffect(() => {
    if (dateRangeMonths !== prevDateRangeMonthsRef.current) {
      prevDateRangeMonthsRef.current = dateRangeMonths;
      shouldUpdateFromMonthsRef.current = true;
    }
  }, [dateRangeMonths]);

  // Track when onClear changes
  useEffect(() => {
    if (onClear && onClear !== prevOnClearRef.current) {
      prevOnClearRef.current = onClear;
      shouldClearRef.current = true;
    }
  }, [onClear]);

  const clearFieldAndDispatchError = (message: string) => {
    dispatch(setSingleAlertObj({ message: message, alertType: "error" }));
  };

  const isValidDate = (dateString: string): boolean => {
    const regex = new RegExp(
      `^([0-1]?[0-9])([/-])([0-3]?[0-9])([/-])([0-9]{4})$`
    );

    const match = dateString.match(regex);
    if (!match) return false;

    const [_, month, separator1, day, separator2, year] = match;

    if (separator1 !== separator2) return false;

    return true;
  };

  const validateAndSanitizeDate = (dateStr: string): DateObject | null => {
    if (!dateStr) return null;

    const parts = dateStr.split("/").map(Number);
    if (parts.length !== 3) {
      return null;
    }

    let [month, day, year] = parts;

    // Validate month
    if (month < 1 || month > 12) {
      clearFieldAndDispatchError("Month must be between 1 and 12.");
      return null;
    }

    // Validate day
    const maxDay = new Date(year, month, 0).getDate();
    if (day < 1 || day > maxDay) {
      clearFieldAndDispatchError(`Day must be between 1 and ${maxDay} for the month.`);
      return null;
    }

    return new DateObject({ date: dateStr, format: "MM/DD/YYYY" });
  };

  const parseDateRange = (value: string): DateObject[] => {
    if (!value) return [];
    const dates = value.split(" - ");

    if (dates.length === 2) {
      // Handle date range
      try {
        return dates.map(d => new DateObject({ date: d, format: "MM/DD/YYYY" }));
      } catch {
        return [];
      }
    } else if (dates.length === 1 && dates[0]) {
      try {
        return [new DateObject({ date: dates[0], format: "MM/DD/YYYY" })];
      } catch {
        return [];
      }
    }
    return [];
  };

  return (
    <Controller
      name={controllerName}
      control={control}
      defaultValue={getDefaultDateRange()}
      render={({ field }) => {
        // Handle updates from dateRangeMonths prop
        if (shouldUpdateFromMonthsRef.current) {
          shouldUpdateFromMonthsRef.current = false;
          const defaultValue = getDefaultDateRange();
          
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            field.onChange(defaultValue);
            
            if (defaultValue && onChange) {
              const dates = defaultValue.split(" - ");
              const fromDate = new DateObject({ date: dates[0], format: "MM/DD/YYYY" });
              const toDate = new DateObject({ date: dates[1], format: "MM/DD/YYYY" });
              onChange(fromDate, toDate);
            } else if (onChange) {
              onChange(null, null);
            }
          }, 0);
        }

        // Handle clear from onClear prop
        if (shouldClearRef.current) {
          shouldClearRef.current = false;
          
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            field.onChange("");
            if (onChange) {
              onChange(null, null);
            }
          }, 0);
        }

        const dateRange = parseDateRange(field.value);
        const inputValue = field.value;

        const handleChange = (dates: DateObject[]) => {
          if (dates.length === 1) {
            const newValue = dates[0].format("MM/DD/YYYY");
            field.onChange(newValue);

            if (onChange) {
              onChange(dates[0], null);
            }
          } else if (dates.length === 2) {
            // Handle date range
            const newValue = dates.map((date) => date.format("MM/DD/YYYY")).join(" - ");
            field.onChange(newValue);

            if (onChange) {
              const [fromDate, toDate] = dates;
              onChange(fromDate, toDate);
            }
          }
        };

        const handleClear = () => {
          field.onChange("");
          if (onChange) {
            onChange(null, null);
          }
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let value = e.target.value;

          const validCharactersRegex = /^[0-9\s/-]*$/;
          if (!validCharactersRegex.test(value)) {
            clearFieldAndDispatchError("Only numbers, spaces, '-' and '/' are allowed.");
            return;
          }

          field.onChange(value);

          if (value.includes(" - ")) {
            // Handle range
            value = value.replace(/\s*-\s*/g, " - ");
            const [startDateStr, endDateStr] = value.split(" - ");
            const start = validateAndSanitizeDate(startDateStr);
            const end = validateAndSanitizeDate(endDateStr);

            if (start && end && onChange) {
              onChange(start, end);
            } else if (onChange) {
              onChange(null, null);
            }
          } else {
            const singleDate = validateAndSanitizeDate(value);
            if (singleDate && onChange) {
              onChange(singleDate, singleDate);
            } else if (onChange) {
              onChange(null, null);
            }
          }
        };

        const handleBlur = () => {
          if (dateRange.length === 1) {
            const [date] = dateRange;
            const dateStr = date.toString();

            if (!isValidDate(dateStr)) {
              const errorObj: SingleAlertInfo = {
                message: "Invalid date format entered.",
                alertType: "error",
              };
              setTimeout(() => {
                dispatch(setSingleAlertObj(errorObj));
              }, 100);
              field.onChange("");
              if (onChange) {
                onChange(null, null);
              }
              return;
            }
          }
          setIsFocused(false);
        };

        return (
          <DatePicker
            numberOfMonths={month || 2}
            value={dateRange}
            onChange={handleChange}
            range
            rangeHover={false}
            shadow={false}
            arrow={false}
            offsetY={8}
            portal
            zIndex={zIndex}
            weekDays={["S", "M", "T", "W", "T", "F", "S"]}
            onOpen={() => setIsFocused(true)}
            onClose={() => {
              setIsFocused(false);
            }}
            format="MM/DD/YYYY"
            className={
              [
                "rmdp-v3",
                theme.palette.mode === "dark" ? "rmdp-v3-dark" : "rmdp-v3-light",
                isMobile ? "rmdp-mobile" : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
            style={{ width: "100%" }}
            containerStyle={{ width: "100%" }}
            mobileLabels={{
              OK: "APPLY",
              CANCEL: "CLOSE",
            }}
            render={(value, openCalendar) => (
              <CustomTextField
                control={control}
                controllerName={controllerName}
                value={inputValue || value}
                placeholder={placeholder || "MM/DD/YYYY - MM/DD/YYYY"}
                onChange={handleInputChange}
                onClick={() => {
                  openCalendar();
                  setIsFocused(true);
                }}
                onBlur={handleBlur}
                onFocus={() => setIsFocused(true)}
                label={label}
                variant="outlined"
                fullWidth
                inputRef={textFieldRef}
                InputLabelProps={{
                  sx: {
                    paddingRight: "40px",
                  },
                  shrink: isFocused || dateRange.length > 0,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          openCalendar();
                          setIsFocused(true);
                        }}
                      >
                        <InsertInvitation />
                      </IconButton>
                      {dateRange.length > 0 && (
                        <IconButton onClick={handleClear} aria-label="clear date range">
                          <Clear />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default CustomDateRange;